import { ActivatedPriceListRecord, activatedPriceListFromJSON } from "common/records/ActivatedPriceList";
import { RootActions, RootState } from "common/reducers";
import { Record, OrderedMap, List } from "immutable";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { receive } from "common/reducers/Utils";
import {
  ActivatedPriceListActions,
  RECEIVE_ACTIVATED_PRICE_LIST,
  RECEIVE_ACTIVATED_PRICE_LISTS,
} from "common/reducers/activatedPriceList.actions";
import { reducer as productReducer } from "common/reducers/product.reducer";
import { ProductActions } from "common/reducers/product.actions";
import { reducer as productOptionGroupReducer } from "common/reducers/productOptionGroup.reducer";
import { ProductOptionGroupActions } from "common/reducers/productOptionGroup.action";
import { reducer as productOptionReducer } from "common/reducers/productOption.reducer";
import { ProductOptionActions } from "common/reducers/productOption.action";
import { reducer as productToOptionGroupLinkReducer } from "common/reducers/productToOptionGroupLink.reducer";
import { ProductToOptionGroupLinkActions } from "common/reducers/productToOptionGroupLink.actions";

export const ActivatedPriceListStateRecord = Record({
  byId: OrderedMap<number, ActivatedPriceListRecord>(),
  // Which APL's snapshot is currently fanned into the GLOBAL stores (productOverviewsById,
  // productOptionGroups/Options.byId, productToOptionGroupLinks.byId). Those stores hold only ONE
  // snapshot at a time and are keyed by product id, so loading a second APL that reuses the same
  // ids overwrites the first. getActivatedPriceList reads this to decide whether the live globals
  // already reflect the requested APL or need a re-fan. See getActivatedPriceList.
  fannedId: null as number | null,
});

export const initialState = ActivatedPriceListStateRecord();

export type ActivatedPriceListState = typeof initialState;

export const model = "activatedPriceLists";

// Deep-copies a product node, computing `ancestral_name`/`ancestral_name_lowercase` top-down
// (e.g. "GAF > Root > Child 1"). The snapshot JSON omits these on children, but Product Selector
// search and the product→line-item name copy both match on ancestral_name. A node opts out of the
// parent prefix via include_parent_names. Input is never mutated.
const annotateProduct = (node: any, parentAncestral: string): any => {
  const name = node.name ?? "";
  const includeParents = node.include_parent_names === undefined ? true : node.include_parent_names;
  const ancestralName = includeParents && parentAncestral ? `${parentAncestral} > ${name}` : name;
  const annotatedChildren = Array.isArray(node.children)
    ? node.children.map((child: any) => annotateProduct(child, ancestralName))
    : node.children;
  return {
    ...node,
    ancestral_name: ancestralName,
    ancestral_name_lowercase: ancestralName.toLowerCase(),
    children: annotatedChildren,
  };
};

const forEachLink = (node: any, callback: (link: any) => void): void => {
  if (Array.isArray(node.ptog_links)) {
    node.ptog_links.forEach(callback);
  }
  if (Array.isArray(node.children)) {
    node.children.forEach((child: any) => forEachLink(child, callback));
  }
};

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ACTIVATED_PRICE_LIST:
      const products = action.payload.activatedPriceList.products;
      const incoming = activatedPriceListFromJSON(action.payload.activatedPriceList);
      if (action.payload.pushToList) {
        state = idsManagementReducer(state, IdsManagementActions.pushId(incoming.id, model));
      }
      if (products) {
        // Fan the snapshot into the global stores. RECEIVE_PRODUCT_OVERVIEW recurses children into
        // productOverviewsById, so every descendant lands keyed by id with its full path.
        const annotatedRoots = products.map((root: any) => annotateProduct(root, ""));
        state = productReducer(state, ProductActions.receiveProductOverviews(annotatedRoots));
        // ptog_links (carrying options.default) are nested at every depth — fan into the global store.
        annotatedRoots.forEach((root: any) => {
          forEachLink(root, (link: any) => {
            state = productToOptionGroupLinkReducer(
              state,
              ProductToOptionGroupLinkActions.receiveProductToOptionGroupLink(link),
            );
          });
        });
        // Price-list-level product_option_groups[] (each with nested options[]).
        const optionGroups = action.payload.activatedPriceList.product_option_groups;
        if (Array.isArray(optionGroups)) {
          optionGroups.forEach((group: any) => {
            state = productOptionGroupReducer(state, ProductOptionGroupActions.receiveProductOptionGroup(group));
            if (Array.isArray(group.options)) {
              group.options.forEach((option: any) => {
                state = productOptionReducer(state, ProductOptionActions.receiveProductOption(option));
              });
            }
          });
        }
      }
      // A SUMMARY payload (no products) must not wipe an existing cached record's snapshot identity
      // (cached + product_ids), else a later listActivatedPriceLists would revert an already-loaded
      // estimate's dropdown to unhydrated. The cached path (incoming has products) always wins.
      const existing = state.getIn([model, "byId", incoming.id]) as ActivatedPriceListRecord | undefined;
      const merged =
        !products && existing && existing.cached
          ? incoming.merge({
              cached: existing.cached,
              product_ids: existing.product_ids,
            })
          : incoming;
      state = receive(state, model, merged) as RootState;
      // Record whose snapshot is now live in the shared global stores, but ONLY when this payload
      // actually fanned products. A SUMMARY payload (no products) leaves the globals untouched, so
      // flipping fannedId here would falsely claim the globals reflect this APL.
      if (products) {
        state = state.setIn([model, "fannedId"], incoming.id) as RootState;
      }
      return state;

    case RECEIVE_ACTIVATED_PRICE_LISTS:
      const activatedPriceLists = List(action.payload.activatedPriceLists);
      activatedPriceLists.forEach((activatedPriceList) => {
        state = reducer(state, ActivatedPriceListActions.receiveActivatedPriceList(activatedPriceList));
      });
      return state;

    default:
      return state;
  }
};
